import { useEffect, useState, type RefObject } from 'react';

const isTextField = (el: EventTarget | null): el is HTMLElement =>
  el instanceof HTMLElement &&
  (el.tagName === 'TEXTAREA' ||
    (el.tagName === 'INPUT' && !['range', 'checkbox', 'radio', 'button', 'file'].includes((el as HTMLInputElement).type)) ||
    el.isContentEditable);

/**
 * Keyboard-safe editor on a phone (spec 9.6).
 *
 * While a text field inside `rootRef` has focus, the editor root is sized to
 * the visual viewport (its height and its offset, since iOS scrolls the
 * layout viewport to show the field), the field is scrolled fully into view
 * above the keyboard, and the page underneath is scroll-locked. The caller
 * renders an opaque cover behind the editor while this returns true, so the
 * lessons page never shows through.
 */
export function useKeyboardSafe(rootRef: RefObject<HTMLElement | null>, enabled: boolean): boolean {
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const root = rootRef.current;
    if (!root) return;
    const onFocusIn = (e: FocusEvent) => { if (isTextField(e.target)) setActive(true); };
    const onFocusOut = (e: FocusEvent) => {
      if (!isTextField(e.target)) return;
      // Focus moving to another field keeps it active.
      setTimeout(() => { if (!isTextField(document.activeElement)) setActive(false); }, 0);
    };
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);
    return () => {
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
    };
  }, [enabled, rootRef]);

  useEffect(() => {
    const root = rootRef.current;
    const vv = window.visualViewport;
    if (!active || !root || !vv) return;
    const html = document.documentElement;
    const prev = {
      top: root.style.top, height: root.style.height, bottom: root.style.bottom,
      htmlOverflow: html.style.overflow, bodyOverflow: document.body.style.overflow,
    };
    /* The phone editor owns the document's scroll for its whole session
       (data-editor-covering, spec 14.11), so the page is not locked here:
       locking it would undo the scroll that keeps Safari's bars down. */
    if (!html.hasAttribute('data-editor-covering')) {
      html.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
    let raf = 0;
    const fit = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        root.style.top = `${vv.offsetTop}px`;
        root.style.bottom = 'auto';
        root.style.height = `${vv.height}px`;
        const field = document.activeElement;
        if (isTextField(field) && root.contains(field)) field.scrollIntoView({ block: 'nearest' });
      });
    };
    fit();
    // The keyboard animates in after focus; settle once it has.
    const late = setTimeout(fit, 350);
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(late);
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
      root.style.top = prev.top;
      root.style.height = prev.height;
      root.style.bottom = prev.bottom;
      html.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
    };
  }, [active, rootRef]);

  return active;
}
